// src/pages/admin/CourseLectures/lectures/AuPairLecture03HostFamilySearch.tsx
import React, { useState, useRef } from "react";
import { InfoBox, AgendaCard, TaskList } from "../../components/LectureUI";

// ── Shared Accordion ──────────────────────────────────────────────
const Accordion: React.FC<{
  id: string;
  num: string;
  icon: string;
  title: string;
  openId: string;
  onToggle: (id: string) => void;
  children: React.ReactNode;
}> = ({ id, num, icon, title, openId, onToggle, children }) => {
  const isOpen = openId === id;
  const ref = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    onToggle(id);
    if (!isOpen) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  return (
    <div
      ref={ref}
      className={`mb-3 rounded-xl border overflow-hidden shadow-sm ${
        isOpen ? "border-[#009fe3]/50" : "border-gray-200"
      } bg-white`}
    >
      <button
        onClick={handleToggle}
        className={`w-full flex items-center gap-3 px-4 py-4 text-left transition-colors ${
          isOpen ? "bg-blue-50" : "hover:bg-gray-50"
        }`}
      >
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2 py-1 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors ${
            isOpen ? "bg-[#009fe3] text-white" : "bg-gray-100 text-gray-500"
          }`}
        >
          <span>{icon}</span>
          <span className="hidden sm:inline">{num}</span>
        </div>
        <span
          className={`flex-1 text-base font-bold ${
            isOpen ? "text-[#0369a1]" : "text-gray-800"
          }`}
        >
          {title}
        </span>
        <span
          className={`flex-shrink-0 text-[#009fe3] transition-transform duration-200 text-sm ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </button>
      {isOpen && (
        <div className="px-4 pb-5 pt-3 border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

// ── Platform Card ─────────────────────────────────────────────────
const PlatformCard: React.FC<{
  rank: string;
  name: string;
  url: string;
  fee: string;
  feeNote: string;
  rating: number;
  tags: string[];
  pros: string[];
  cons: string[];
  color: string;
  highlight?: boolean;
}> = ({
  rank,
  name,
  url,
  fee,
  feeNote,
  rating,
  tags,
  pros,
  cons,
  color,
  highlight,
}) => (
  <div
    className={`rounded-xl border-2 overflow-hidden ${highlight ? "border-[#009fe3]" : "border-gray-200"}`}
  >
    {highlight && (
      <div className="bg-[#009fe3] px-3 py-1 flex items-center justify-between">
        <p className="text-sm font-bold text-white uppercase tracking-wide">
          ⭐ Most Recommended
        </p>
        <p className="text-sm text-white opacity-80">Used by G2G students</p>
      </div>
    )}
    <div className={`p-3 ${color}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-white/70 flex items-center justify-center text-base font-extrabold flex-shrink-0">
            {rank}
          </div>
          <div>
            <p className="text-base font-extrabold">{name}</p>
            <a
              href={`https://www.${url}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-[#009fe3] underline font-semibold"
            >
              {url}
            </a>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-base font-extrabold">{fee}</p>
          <p className="text-sm opacity-60">{feeNote}</p>
        </div>
      </div>

      {/* Stars */}
      <div className="flex items-center gap-0.5 mb-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <span
            key={i}
            className={`text-sm ${i < rating ? "text-amber-400" : "text-gray-300"}`}
          >
            ★
          </span>
        ))}
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-1 mb-3">
        {tags.map((tag, i) => (
          <span
            key={i}
            className="text-sm font-bold bg-white/60 px-2 py-0.5 rounded-full border border-white/80"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="bg-green-50 border border-green-200 rounded-lg p-2">
          <p className="text-sm font-bold text-green-700 mb-1">✅ Pros</p>
          {pros.map((p, i) => (
            <p
              key={i}
              className="text-sm text-green-700 flex items-start gap-1"
            >
              <span className="flex-shrink-0">›</span>
              {p}
            </p>
          ))}
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
          <p className="text-sm font-bold text-red-700 mb-1">⚠️ Cons</p>
          {cons.map((c, i) => (
            <p key={i} className="text-sm text-red-700 flex items-start gap-1">
              <span className="flex-shrink-0">›</span>
              {c}
            </p>
          ))}
        </div>
      </div>
    </div>
  </div>
);

// ── Message Template Card ─────────────────────────────────────────
const MessageTemplate: React.FC<{
  title: string;
  language: string;
  content: string;
  color: string;
}> = ({ title, language, content, color }) => (
  <div className={`rounded-xl border overflow-hidden ${color}`}>
    <div className="px-3 py-2 flex items-center justify-between border-b border-white/40">
      <p className="text-sm font-bold">{title}</p>
      <span className="text-sm font-bold bg-white/30 px-2 py-0.5 rounded-full">
        {language}
      </span>
    </div>
    <div className="bg-white px-4 py-3">
      <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed font-mono">
        {content}
      </p>
    </div>
  </div>
);

// ── Step Card ─────────────────────────────────────────────────────
const StepCard: React.FC<{
  step: string;
  title: string;
  desc: string;
  color: string;
  dot: string;
}> = ({ step, title, desc, color, dot }) => (
  <div className={`rounded-xl border p-3 ${color}`}>
    <div className="flex items-start gap-3">
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center text-base font-extrabold flex-shrink-0 text-white ${dot}`}
      >
        {step}
      </div>
      <div>
        <p className="text-sm font-bold mb-0.5">{title}</p>
        <p className="text-sm opacity-80">{desc}</p>
      </div>
    </div>
  </div>
);

// ── Hack Card ─────────────────────────────────────────────────────
const HackCard: React.FC<{
  icon: string;
  title: string;
  desc: string;
  action: string;
}> = ({ icon, title, desc, action }) => (
  <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
    <div className="flex items-start gap-2.5 mb-2">
      <span className="text-xl flex-shrink-0">{icon}</span>
      <div>
        <p className="text-sm font-bold text-purple-800">{title}</p>
        <p className="text-sm text-purple-700 opacity-80 mt-0.5">{desc}</p>
      </div>
    </div>
    <div className="bg-white rounded-lg border border-purple-200 px-3 py-2">
      <p className="text-sm font-bold text-purple-600 uppercase tracking-wide mb-0.5">
        Action Step
      </p>
      <p className="text-sm text-purple-700">{action}</p>
    </div>
  </div>
);

// ── Main Component ────────────────────────────────────────────────
const AuPairLecture03HostFamilySearch: React.FC = () => {
  const [openSection, setOpenSection] = useState<string>("");
  const toggle = (id: string) =>
    setOpenSection((prev) => (prev === id ? "" : id));

  return (
    <div>
      {/* Course Header Banner */}
      <div className="bg-gradient-to-r from-[#009fe3] to-[#0369a1] rounded-xl px-4 py-4 mb-4">
        <p className="text-sm font-bold uppercase tracking-widest text-white opacity-70 mb-1">
          Au Pair Preparatory Course
        </p>
        <p className="text-base font-extrabold text-white">
          Session 03 — Host Family Finding Strategy
        </p>
        <p className="text-sm text-white opacity-80 mt-1">
          Designed by Guideline2Germany
        </p>
      </div>

      {/* Recap */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 mb-4">
        <p className="text-sm font-bold text-amber-700 uppercase tracking-wide mb-2">
          📋 Tasks from Session 02 — done?
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            "Children photo taken & submitted to G2G",
            "Household work photo taken & submitted to G2G",
            "Profile photo taken — bright, warm, natural",
            "Motivation paragraph written & submitted for feedback",
            "Au Pair profile draft started — all fields filled",
          ].map((t, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 text-sm text-amber-800"
            >
              <span className="w-4 h-4 rounded border border-amber-400 flex-shrink-0 bg-white" />
              {t}
            </div>
          ))}
        </div>
      </div>

      <AgendaCard
        items={[
          { num: "1", label: "Overview — How Host Family Search Works" },
          { num: "2", label: "Platform 1 — AuPair.com (Recommended)" },
          { num: "3", label: "Other Platforms — Fees & Comparison" },
          { num: "4", label: "Platform Fees Explained — 150–200€ Breakdown" },
          { num: "5", label: "How to Write the First Message" },
          { num: "5b", label: "AuPair.com Profile Questions — Sample Answers" },
          { num: "6", label: "Hack — Social Media Strategy" },
          { num: "7", label: "Red Flags — Families to Avoid" },
          { num: "8", label: "Step-by-Step Search Action Plan" },
        ]}
      />

      {/* 01 — How it Works */}
      <Accordion
        id="01"
        num="SECTION 01"
        icon="🔍"
        title="Overview — How Host Family Search Works"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-3 mb-4">
          <p className="text-sm font-bold text-[#0369a1] mb-1">
            The search works both ways — you apply to families AND families
            apply to you.
          </p>
          <p className="text-sm text-[#0369a1]">
            On most platforms, host families post their profile and Au Pairs
            post their profile. Both sides browse and send messages. The key is
            to be active, fast, and highly personalized in every message.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          {[
            {
              icon: "👤",
              title: "You create a profile",
              desc: "Upload photos, write your motivation, list your childcare experience and language skills",
              color: "bg-blue-50 border-blue-200 text-blue-800",
            },
            {
              icon: "🔍",
              title: "You browse families",
              desc: "Search by city, number of children, start date and family preferences",
              color: "bg-purple-50 border-purple-200 text-purple-800",
            },
            {
              icon: "✉️",
              title: "You send a message",
              desc: "A personalized, warm first message — not a copy-paste template. Families can also contact you.",
              color: "bg-green-50 border-green-200 text-green-800",
            },
          ].map((item, i) => (
            <div key={i} className={`rounded-xl border p-3 ${item.color}`}>
              <span className="text-2xl block mb-1">{item.icon}</span>
              <p className="text-sm font-bold mb-0.5">{item.title}</p>
              <p className="text-sm opacity-80">{item.desc}</p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-bold text-amber-800 mb-2">
            ⏰ How long does it take to find a family?
          </p>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              {
                label: "Fast",
                value: "2–4 weeks",
                note: "Active daily search + quick replies",
                color: "text-green-700",
              },
              {
                label: "Average",
                value: "1–3 months",
                note: "Normal paced search",
                color: "text-amber-700",
              },
              {
                label: "Slow",
                value: "3–6 months",
                note: "Inactive or weak profile",
                color: "text-red-600",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-amber-200 px-2 py-2"
              >
                <p className={`text-xl font-extrabold ${item.color}`}>
                  {item.value}
                </p>
                <p className="text-sm font-bold text-amber-800">{item.label}</p>
                <p className="text-sm text-gray-500 mt-0.5">{item.note}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 02 — AuPair.com */}
      <Accordion
        id="02"
        num="SECTION 02"
        icon="⭐"
        title="Platform 1 — AuPair.com (Most Recommended)"
        openId={openSection}
        onToggle={toggle}
      >
        <PlatformCard
          rank="1"
          name="AuPair.com"
          url="aupair.com"
          fee="~150–200 € / year"
          feeNote="Au Pair pays — one-time fee"
          rating={5}
          tags={[
            "Largest Database",
            "Verified Families",
            "Video Introduction",
            "Most Used in Germany",
          ]}
          pros={[
            "Largest number of German host families",
            "Family profiles are verified",
            "Video intro feature — stand out immediately",
            "Filter by city, family size, start date",
            "Most G2G students found family here",
          ]}
          cons={[
            "Annual fee of 150–200€ required to message",
            "High competition — many Au Pairs on the platform",
            "Some families inactive — check 'last active' date",
          ]}
          color="bg-blue-50 border-blue-200 text-blue-800"
          highlight
        />

        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm font-bold text-blue-800 mb-2">
            💡 How to stand out on AuPair.com
          </p>
          <div className="space-y-1.5">
            {[
              "Upload a video introduction — families who see a video are 3x more likely to respond",
              "Complete every single profile field — incomplete profiles are filtered out",
              "Log in daily — active profiles are shown higher in search results",
              "Reply to families within 2 hours — fast replies show enthusiasm",
              "Personalize every message — mention the children's names and ages from their profile",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
              >
                <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>
                <p className="text-sm text-blue-700">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 03 — Other Platforms */}
      <Accordion
        id="03"
        num="SECTION 03"
        icon="🌐"
        title="Other Platforms — Full Comparison"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Use AuPair.com as your primary platform and one or two others as
          backup. Being on multiple platforms increases your chances
          significantly.
        </p>
        <div className="space-y-3">
          <PlatformCard
            rank="2"
            name="AuPairWorld.net"
            url="aupairworld.net"
            fee="Free basic / ~60€ premium"
            feeNote="Cheapest paid option"
            rating={4}
            tags={["Free Option", "Large Database", "Good for Beginners"]}
            pros={[
              "Free basic membership — good to start",
              "Large international database",
              "Easy to use interface",
            ]}
            cons={[
              "Bangldeshis are not allowed",
              "Free version limits messaging significantly",
              "Fewer German families than AuPair.com",
              "Less verification of family profiles",
            ]}
            color="bg-green-50 border-green-200 text-green-800"
          />

          <PlatformCard
            rank="3"
            name="Greataupair.com"
            url="greataupair.com"
            fee="~80–120 € / year"
            feeNote="Mid-range pricing"
            rating={3}
            tags={["International", "Mid-Range Fee", "Good Backup Option"]}
            pros={[
              "Good number of German families",
              "Lower fee than AuPair.com",
              "Detailed search filters",
            ]}
            cons={[
              "Smaller community than AuPair.com",
              "Less active — some profiles outdated",
              "Support can be slow",
            ]}
            color="bg-purple-50 border-purple-200 text-purple-800"
          />

          <PlatformCard
            rank="4"
            name="Findaupair.com"
            url="findaupair.com"
            fee="~50–80 € / year"
            feeNote="Budget option"
            rating={3}
            tags={["Budget-Friendly", "Simple Interface", "Smaller Database"]}
            pros={[
              "Most affordable paid option",
              "Simple and straightforward",
              "Good for quick profile setup",
            ]}
            cons={[
              "Much smaller database than AuPair.com",
              "Fewer German host families specifically",
              "Less traffic — slower responses",
            ]}
            color="bg-amber-50 border-amber-200 text-amber-800"
          />
        </div>
      </Accordion>

      {/* 04 — Fees Explained */}
      <Accordion
        id="04"
        num="SECTION 04"
        icon="💶"
        title="Platform Fees Explained — 150–200€ Breakdown"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4">
          <p className="text-sm font-bold text-amber-800 mb-1">
            Is the fee worth it?
          </p>
          <p className="text-sm text-amber-700">
            Yes — without paying, you cannot send messages to host families on
            AuPair.com. The fee gives you 12 months of full access. Compared to
            the cost of your flight and visa, this is a small investment with a
            huge return.
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden mb-4">
          <div className="bg-gray-100 px-3 py-2">
            <p className="text-sm font-bold text-gray-600 uppercase tracking-wide">
              Fee Comparison Table
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            {[
              {
                platform: "AuPair.com",
                fee: "150–200 €",
                access: "Full — 12 months",
                recommended: true,
              },
              {
                platform: "AuPairWorld.net",
                fee: "Free / 60€",
                access: "Limited free / Full premium",
                recommended: false,
              },
              {
                platform: "Greataupair.com",
                fee: "80–120 €",
                access: "Full — 12 months",
                recommended: false,
              },
              {
                platform: "Findaupair.com",
                fee: "50–80 €",
                access: "Full — 12 months",
                recommended: false,
              },
            ].map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 px-3 py-2.5 text-sm ${row.recommended ? "bg-blue-50" : "bg-white"}`}
              >
                <p
                  className={`font-bold ${row.recommended ? "text-[#0369a1]" : "text-gray-700"}`}
                >
                  {row.platform}
                  {row.recommended && (
                    <span className="ml-1 text-sm bg-[#009fe3] text-white px-1 py-0.5 rounded">
                      Best
                    </span>
                  )}
                </p>
                <p className="text-gray-600 font-semibold">{row.fee}</p>
                <p className="text-gray-500">{row.access}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] px-4 py-3">
          <p className="text-sm font-bold text-[#0369a1] mb-2">
            💡 Smart Fee Strategy
          </p>
          <div className="space-y-1.5">
            {[
              "Start with free account — build and test your profile at no cost",
              "Once profile is reviewed and approved by G2G — then pay for AuPair.com",
              "Pay for AuPair.com only when your profile, photos and motivation are 100% ready",
              "Do NOT pay and then have a weak profile — you waste the subscription window",
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-white/60 rounded-lg px-2 py-1"
              >
                <span className="text-[#009fe3] font-bold flex-shrink-0 mt-0.5">
                  ›
                </span>
                <p className="text-sm text-[#0369a1]">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </Accordion>

      {/* 05 — First Message */}
      <Accordion
        id="05"
        num="SECTION 05"
        icon="✉️"
        title="How to Write the First Message"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-4">
          <p className="text-sm font-bold text-purple-800 mb-1">
            Your first message is everything — most Au Pairs send copy-paste. Do
            not be like them.
          </p>
          <p className="text-sm text-purple-700">
            Host families receive dozens of identical messages every week. A
            personalized message that mentions their specific children,
            location, or interests stands out immediately and gets replies.
          </p>
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-2">
          Message Formula
        </p>
        <div className="space-y-2 mb-4">
          {[
            {
              step: "1",
              label: "Warm greeting",
              desc: "Use their name if available — 'Dear Family Müller'",
              color: "bg-blue-50 border-blue-200 text-blue-800",
              dot: "bg-[#009fe3]",
            },
            {
              step: "2",
              label: "Personal reference",
              desc: "Mention something specific from their profile — city, children's ages, hobbies",
              color: "bg-purple-50 border-purple-200 text-purple-800",
              dot: "bg-purple-500",
            },
            {
              step: "3",
              label: "Short introduction",
              desc: "2–3 sentences about who you are and your childcare experience",
              color: "bg-teal-50 border-teal-200 text-teal-800",
              dot: "bg-teal-500",
            },
            {
              step: "4",
              label: "Why this family",
              desc: "One sentence on why you want to work specifically with them",
              color: "bg-green-50 border-green-200 text-green-800",
              dot: "bg-green-500",
            },
            {
              step: "5",
              label: "Call to action",
              desc: "Invite them to read your profile and suggest a video call",
              color: "bg-amber-50 border-amber-200 text-amber-800",
              dot: "bg-amber-500",
            },
          ].map((item) => (
            <StepCard
              key={item.step}
              step={item.step}
              title={item.label}
              desc={item.desc}
              color={item.color}
              dot={item.dot}
            />
          ))}
        </div>

        <p className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-2">
          Example Messages
        </p>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 mb-3">
          <p className="text-sm font-bold text-amber-800 mb-1">
            ✍️ The rule: short, specific, memorable
          </p>
          <p className="text-sm text-amber-700">
            Host families receive 50+ messages a week. A short, creative,
            personal message gets read. A long formal introduction gets skipped.
            Keep it under 5 sentences. Make them feel something.
          </p>
        </div>
        <div className="space-y-3">
          <MessageTemplate
            title="Strong First Message — English"
            language="English"
            color="bg-green-500 text-white border-green-400"
            content={`Dear Family Müller,

Two kids, Munich, and you need someone who genuinely enjoys rainy Tuesday mornings — I think that might be me.

I am Fatima, 22, from Bangladesh. I care for my younger cousins regularly and currently tutor two children after school. My German is A2 and I am starting B1 soon — though honestly, I learn fastest when a 6-year-old is correcting me.

Would you be open to a short video call?

Warm regards, Fatima`}
          />

          <MessageTemplate
            title="Strong First Message — German (use with Google Translate / ChatGPT)"
            language="Deutsch"
            color="bg-[#0369a1] text-white border-[#0369a1]"
            content={`Liebe Familie Müller,

zwei Kinder, München, und Sie brauchen jemanden, der regnerische Dienstagmorgen wirklich mag — das könnte ich sein.

Ich bin Fatima, 22, aus Bangladesch. Ich kümmere mich regelmäßig um meine jüngeren Cousins und gebe zwei Kindern Nachhilfe. Mein Deutsch ist auf A2-Niveau — aber ich lerne am schnellsten, wenn mich ein 6-jähriges Kind korrigiert.

Hätten Sie Lust auf einen kurzen Videoanruf?

Herzliche Grüße, Fatima`}
          />

          <div className="rounded-xl border border-red-200 overflow-hidden">
            <div className="bg-red-500 px-3 py-1.5 flex items-center justify-between">
              <p className="text-sm font-bold text-white">
                ❌ Weak Message — Never send this
              </p>
              <span className="text-sm text-white opacity-80">
                Copy-paste template
              </span>
            </div>
            <div className="bg-red-50 px-4 py-3">
              <p className="text-sm text-red-700 italic font-mono whitespace-pre-line">
                {`Hello,

I want to be an au pair in Germany. I love children and I am a hard worker. Please check my profile.

Thanks`}
              </p>
            </div>
            <div className="px-3 py-2 bg-white border-t border-red-100">
              <p className="text-sm text-red-600">
                ✗ No name &nbsp;✗ Not personalized &nbsp;✗ Too short &nbsp;✗ No
                call to action
              </p>
            </div>
          </div>
        </div>
      </Accordion>

      {/* 05b — Profile Questions */}
      <Accordion
        id="05b"
        num="SECTION 05B"
        icon="📝"
        title="AuPair.com Profile Questions — Sample Answers"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] p-4 mb-4">
          <p className="text-base font-bold text-[#0369a1] mb-2">
            AuPair.com has 3 profile text fields that host families read most
            carefully.
          </p>
          <p className="text-sm text-[#0369a1]">
            Below are sample answers written for a real G2G student. Use them to
            understand the tone and structure — then write your own version.
            Never copy them word for word.
          </p>
        </div>

        {/* Field 1 — Motivationsbrief */}
        <div className="rounded-xl border-2 border-[#009fe3] overflow-hidden mb-4">
          <div className="bg-[#009fe3] px-4 py-2.5">
            <p className="text-sm font-bold text-white">
              Frage 1 — Motivationsbrief an die Gastfamilie
            </p>
            <p className="text-xs text-white opacity-80 mt-0.5">
              Answer all 4 questions in one flowing cover letter: Wer bist du?
              Warum Au Pair? Warum du? Wie bist du qualifiziert?
            </p>
          </div>
          <div className="bg-white px-5 py-5">
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed font-mono">
              {`Liebe Gastfamilie,

kennen Sie das Gefühl, wenn ein Kind Sie an der Hand nimmt und sagt: „Komm, ich zeig dir was"? Diesen Moment — da bin ich in meinem Element.

Ich heiße Nadia, komme aus Bangladesch und studiere im dritten Jahr. Ich bin die Älteste von fünf Geschwistern. Das klingt nach einer Zahl — aber es bedeutet: Ich habe Kindern beim Laufen gelernt zugeschaut, habe Hausaufgaben erklärt, Tränen getröstet, Streit geschlichtet. Nicht als Job, sondern als Teil meines Lebens. Ich weiß, was Kinder brauchen — weil ich es immer schon gespürt habe.

Ich helfe meiner Mutter täglich im Haushalt: kochen, putzen, Wäsche machen. Ich sage das nicht, um einen guten Eindruck zu hinterlassen — sondern damit Sie wissen, dass ich nicht erst ankommen muss, um loszulegen.

Mein Deutsch ist A2 — aber ich lerne jeden Tag. Ich höre Podcasts, schaue Serien, übe mit Apps. Und ich glaube fest, dass ein Jahr in einer deutschen Familie mehr bringt als drei Jahre Unterricht.

Darf ich Sie bei einem kurzen Videogespräch kennenlernen? Ich freue mich, mehr über Ihre Familie zu erfahren — und Ihnen zu zeigen, wer ich wirklich bin.

Von Herzen,
Nadia`}
            </p>
          </div>
          <div className="bg-blue-50 border-t border-blue-100 px-4 py-3">
            <p className="text-sm font-bold text-[#0369a1] mb-1">
              💡 Why this works
            </p>
            <div className="space-y-1">
              {[
                "Opens with a vivid scene — host family can picture you immediately",
                "All 4 questions answered naturally in one letter — not as a list",
                "Specific details (cats, plants, cousins, Nachhilfe) feel real and trustworthy",
                "Ends with an invitation for a video call — moves the conversation forward",
              ].map((t, i) => (
                <p
                  key={i}
                  className="text-sm text-[#0369a1] flex items-start gap-1.5"
                >
                  <span className="flex-shrink-0">›</span>
                  {t}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Field 2 — Au Pair Experience + Motivation */}
        <div className="rounded-xl border-2 border-teal-400 overflow-hidden mb-4">
          <div className="bg-teal-600 px-4 py-2.5">
            <p className="text-sm font-bold text-white">
              Frage 2 — Au-Pair-Erfahrung & Motivation für den Aufenthalt
            </p>
            <p className="text-xs text-white opacity-80 mt-0.5">
              Describe your childcare experience and explain why you want to
              live in Germany
            </p>
          </div>
          <div className="bg-white px-5 py-5">
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed font-mono">
              {`Als Au Pair war ich noch nicht tätig — aber Kinder sind seit Jahren ein fester Teil meines Alltags. Meine vier jüngeren Geschwister sind mit mir aufgewachsen. Ich habe morgens ihre Schultaschen gepackt, nachmittags bei den Hausaufgaben geholfen, abends Gutenachtgeschichten vorgelesen. Nicht als Pflicht — einfach weil es dazugehörte.

Und genau das möchte ich auch in einer Gastfamilie tun: dazugehören. Nicht als Hilfe, die morgens kommt und abends geht — sondern als jemand, der wirklich da ist. Der weiß, wann ein Kind eine Umarmung braucht und wann es einfach in Ruhe spielen möchte.

Deutschland ist für mich kein Abenteuer auf Zeit. Ich möchte die Sprache nicht aus dem Wörterbuch lernen, sondern beim Einkaufen, beim Kochen, beim Spaßhaben mit Kindern. Dieses Jahr soll kein Auslandsaufenthalt werden — es soll ein echter Neuanfang sein.`}
            </p>
          </div>
          <div className="bg-teal-50 border-t border-teal-100 px-4 py-3">
            <p className="text-sm font-bold text-teal-700 mb-1">
              💡 Why this works
            </p>
            <div className="space-y-1">
              {[
                "Honest about no prior Au Pair position — then immediately counters with real experience",
                "The parent feedback detail ('Kinder fühlen sich wohl') is specific and credible",
                "Motivation is framed as giving, not just receiving — families respond to this",
              ].map((t, i) => (
                <p
                  key={i}
                  className="text-sm text-teal-700 flex items-start gap-1.5"
                >
                  <span className="flex-shrink-0">›</span>
                  {t}
                </p>
              ))}
            </div>
          </div>
        </div>

        {/* Field 3 — About me + Future plans */}
        <div className="rounded-xl border-2 border-indigo-300 overflow-hidden mb-4">
          <div className="bg-indigo-600 px-4 py-2.5">
            <p className="text-sm font-bold text-white">
              Frage 3 — Über mich: Interessen, Familie & Zukunftspläne
            </p>
            <p className="text-xs text-white opacity-80 mt-0.5">
              Describe yourself, your hobbies, your family and your plans after
              Au Pair
            </p>
          </div>
          <div className="bg-white px-5 py-5">
            <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed font-mono">
              {`Ich bin neugierig, ziemlich direkt und habe einen Sinn für Humor — auch wenn mein Deutsch das noch nicht immer zeigen kann. Ich komme aus einer großen, lauten Familie in Dhaka: Fünf Personen, eine Küche, viele Meinungen. Ich habe früh gelernt, zuzuhören, Kompromisse zu finden — und trotzdem meine eigene Stimme zu behalten.

In meiner Freizeit koche ich viel, am liebsten Dinge, die ich noch nie gemacht habe. Ich höre Podcasts auf Deutsch, um die Sprache zu üben — meistens verstehe ich die Hälfte, aber die Hälfte wird jeden Tag mehr. Außerdem nähe ich gerne, spiele Badminton und kümmere mich um einen kleinen Gemüsegarten auf dem Dach unseres Hauses. Geduld und Sorgfalt — das haben mir Pflanzen beigebracht.

Ich lebe gerne in Strukturen, die sich lebendig anfühlen. Nicht starr — aber verlässlich.

Nach dem Au-Pair-Jahr möchte ich eine Ausbildung in Deutschland beginnen — wahrscheinlich im sozialen oder pädagogischen Bereich. Das ist kein Plan, der gestern entstanden ist. Ich arbeite seit zwei Jahren darauf hin: mit Sprachkursen, mit Recherche, mit diesem Kurs. Das Au-Pair-Jahr ist für mich der erste richtige Schritt — und ich nehme ihn ernst.`}
            </p>
          </div>
          <div className="bg-indigo-50 border-t border-indigo-100 px-4 py-3">
            <p className="text-sm font-bold text-indigo-700 mb-1">
              💡 Why this works
            </p>
            <div className="space-y-1">
              {[
                "Personal details (cats, plants, brother) make you feel like a real person, not a profile",
                "Future plans show maturity — you have a direction, not just a wish to travel",
                "The last sentence reframes Au Pair as a step, not an escape — families trust this",
              ].map((t, i) => (
                <p
                  key={i}
                  className="text-sm text-indigo-700 flex items-start gap-1.5"
                >
                  <span className="flex-shrink-0">›</span>
                  {t}
                </p>
              ))}
            </div>
          </div>
        </div>

        <InfoBox
          variant="warning"
          icon="⚠️"
          title="These are example answers — do NOT copy"
        >
          Host families and platforms can detect duplicate texts. Use the tone,
          structure and approach as your guide — then write your own version
          with your real experiences, your real family, your real plans.
        </InfoBox>
      </Accordion>

      {/* 06 — Social Media Hack */}
      <Accordion
        id="06"
        num="SECTION 06"
        icon="📱"
        title="Hack — Social Media Strategy (Free & Powerful)"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-purple-200 bg-purple-50 p-3 mb-4">
          <p className="text-sm font-bold text-purple-800 mb-1">
            🚀 The secret most Au Pairs do not use
          </p>
          <p className="text-sm text-purple-700">
            While everyone is competing on AuPair.com, host families are also
            actively posting on Facebook groups, Instagram, and Reddit — for
            free. These channels have less competition and often more genuine
            families.
          </p>
        </div>

        <div className="space-y-3 mb-4">
          <HackCard
            icon="👥"
            title="Facebook Groups — Highest Success Rate"
            desc="Dozens of active Facebook groups connect Au Pairs and host families directly — no platform fee required."
            action={`Search these groups on Facebook:
• "Au Pair Germany 2025"
• "Au Pair Host Family Germany"
• "Bangladeshi Au Pair Germany"
• "Au Pair München / Berlin / Hamburg"
Join all groups and post your profile introduction once a week.`}
          />

          <HackCard
            icon="📸"
            title="Instagram — Build a Visible Presence"
            desc="Some host families and Au Pair communities are active on Instagram. A dedicated Au Pair Instagram makes you searchable."
            action={`Create a separate Instagram account (not your personal one):
• Username: aupair.[yourname].germany
• Bio: "Au Pair candidate from Bangladesh 🇧🇩🇩🇪 | A2 German | Available [Date]"
• Post: your profile photo, children activity photos, German learning updates
• Hashtags: #aupair #aupairgermany #aupair2025 #hostfamily #aupairlife`}
          />

          <HackCard
            icon="💬"
            title="Reddit — r/aupair Community"
            desc="An active international community of current and future Au Pairs and host families."
            action={`Go to reddit.com/r/aupair
• Read host family posts — understand what they look for
• Post your profile for feedback from current Au Pairs
• Ask specific questions — the community is helpful and honest
• Some host families post directly here looking for Au Pairs`}
          />

          <HackCard
            icon="💼"
            title="LinkedIn — Professional Angle"
            desc="Less common but effective for families looking for mature, professional Au Pairs."
            action={`Update your LinkedIn profile:
• Add "Au Pair Candidate — Germany" in your headline
• Write a short 'About' section in English and German
• Connect with Au Pair agencies and groups on LinkedIn
• Search "host family Germany" or "Au Pair Germany"`}
          />

          <HackCard
            icon="🤝"
            title="G2G Community Network — Ask for Referrals"
            desc="Other G2G students who are already in Germany or have completed Au Pair may know families looking for a new Au Pair."
            action={`Contact G2G:
• Ask if any G2G alumni know host families whose current Au Pair is leaving
• A personal referral skips the entire platform process
• Families trust referred Au Pairs much more than cold applications`}
          />
        </div>

        <InfoBox variant="tip" icon="⚡" title="Combine Everything">
          The winning strategy is: AuPair.com (paid, primary) + AuPairWorld.net
          (free backup) + Facebook Groups (free, high success) + Instagram
          presence (free, long-term). Most successful students use all four
          simultaneously.
        </InfoBox>
      </Accordion>

      {/* 07 — Red Flags */}
      <Accordion
        id="07"
        num="SECTION 07"
        icon="🚨"
        title="Red Flags — Families to Approach with Caution"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 mb-4">
          <p className="text-sm font-bold text-red-800 mb-1">
            Not every family on the platform is a good family.
          </p>
          <p className="text-sm text-red-700">
            These warning signs during the search phase — before you even have a
            video call — should make you pause, ask more questions, or move on.
            Session 05 covers the full selection strategy in depth.
          </p>
        </div>
        <div className="space-y-2 mb-4">
          {[
            {
              flag: "Profile last active more than 3 months ago",
              reason:
                "Family may have already found an Au Pair — wasting your message quota",
              risk: "Low",
              riskColor: "bg-amber-100 text-amber-700",
            },
            {
              flag: "No family photo uploaded",
              reason:
                "Genuine families are proud to show themselves. Anonymous profiles are a red flag.",
              risk: "Medium",
              riskColor: "bg-orange-100 text-orange-700",
            },
            {
              flag: "Profile description is copy-pasted and generic",
              reason:
                "Shows the family is not serious or is using the platform carelessly",
              risk: "Medium",
              riskColor: "bg-orange-100 text-orange-700",
            },
            {
              flag: "They ask you to pay money directly before any contract",
              reason:
                "Scam. Legitimate families never ask Au Pairs for direct payment.",
              risk: "High",
              riskColor: "bg-red-100 text-red-700",
            },
            {
              flag: "They expect more than 30 hours / week of work",
              reason:
                "German law limits Au Pair hours to 30 per week maximum. More is illegal.",
              risk: "High",
              riskColor: "bg-red-100 text-red-700",
            },
            {
              flag: "They communicate aggressively or rush you to decide immediately",
              reason:
                "Good families give you time to think. Pressure is a manipulation tactic.",
              risk: "High",
              riskColor: "bg-red-100 text-red-700",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="rounded-xl border border-gray-200 bg-white overflow-hidden"
            >
              <div className="flex items-start gap-3 px-3 py-2.5">
                <span className="text-red-500 font-bold flex-shrink-0 mt-0.5">
                  ⚠️
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap mb-0.5">
                    <p className="text-sm font-bold text-gray-800">
                      {item.flag}
                    </p>
                    <span
                      className={`text-sm font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${item.riskColor}`}
                    >
                      {item.risk} risk
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{item.reason}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <InfoBox
          variant="info"
          icon="📌"
          title="Full Family Selection Strategy in Session 05"
        >
          Session 05 covers exactly how to evaluate a host family before
          accepting — including video call questions, contract checks, and
          dealing with single-father situations. For now, focus on finding
          families — evaluation comes next.
        </InfoBox>
      </Accordion>

      {/* 08 — Action Plan */}
      <Accordion
        id="08"
        num="SECTION 08"
        icon="🗺️"
        title="Step-by-Step Search Action Plan"
        openId={openSection}
        onToggle={toggle}
      >
        <p className="text-sm text-gray-500 mb-4">
          Follow this exact sequence. Do not skip steps — each one builds on the
          previous.
        </p>
        <div className="space-y-2 mb-4">
          {[
            {
              step: "1",
              title: "Profile ready — approved by G2G",
              desc: "Do not start searching until your photos, motivation and profile text have been reviewed. A weak profile wastes your paid subscription.",
              color: "bg-blue-50 border-blue-200 text-blue-800",
              dot: "bg-[#009fe3]",
            },
            {
              step: "2",
              title:
                "Create free accounts on AuPairWorld.net + Greataupair.com",
              desc: "Start with free platforms to test your profile and receive your first messages. Adjust based on family responses.",
              color: "bg-green-50 border-green-200 text-green-800",
              dot: "bg-green-500",
            },
            {
              step: "3",
              title: "Join all Facebook Au Pair Germany groups",
              desc: "Post your short introduction once a week. Reply to any host family posts within 1 hour.",
              color: "bg-purple-50 border-purple-200 text-purple-800",
              dot: "bg-purple-500",
            },
            {
              step: "4",
              title: "Create Au Pair Instagram profile",
              desc: "Set up your dedicated account, post your photos, add hashtags. This takes one day and works passively.",
              color: "bg-pink-50 border-pink-200 text-pink-800",
              dot: "bg-pink-500",
            },
            {
              step: "5",
              title: "Pay for AuPair.com subscription",
              desc: "Once your profile is proven and tested — invest in the primary platform. Send 5–10 personalized messages per day.",
              color: "bg-amber-50 border-amber-200 text-amber-800",
              dot: "bg-amber-500",
            },
            {
              step: "6",
              title: "Send 5–10 personalized messages per day",
              desc: "Check 'last active' date first. Personalize every message — mention the family's specific children and city.",
              color: "bg-teal-50 border-teal-200 text-teal-800",
              dot: "bg-teal-500",
            },
            {
              step: "7",
              title: "Reply to ALL messages within 2 hours",
              desc: "Speed of response signals enthusiasm. Families are comparing multiple candidates — be the fastest and warmest.",
              color: "bg-orange-50 border-orange-200 text-orange-800",
              dot: "bg-orange-500",
            },
            {
              step: "8",
              title: "Shortlist 2–3 families for video call (Session 04)",
              desc: "Once you have interest from families, move to the video call phase. Do not commit to one family too early.",
              color: "bg-indigo-50 border-indigo-200 text-indigo-800",
              dot: "bg-indigo-500",
            },
          ].map((item) => (
            <StepCard
              key={item.step}
              step={item.step}
              title={item.title}
              desc={item.desc}
              color={item.color}
              dot={item.dot}
            />
          ))}
        </div>

        <div className="rounded-xl border border-[#009fe3]/40 bg-[#e0f2fe] px-4 py-3">
          <p className="text-sm font-bold text-[#0369a1] mb-1">
            📊 Realistic Numbers to Expect
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center mt-2">
            {[
              {
                num: "50–100",
                label: "Messages sent",
                color: "text-[#009fe3]",
              },
              {
                num: "10–20",
                label: "Replies received",
                color: "text-purple-600",
              },
              { num: "3–5", label: "Video calls", color: "text-green-600" },
              {
                num: "1",
                label: "Host family found ✓",
                color: "text-amber-600",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl border border-blue-100 px-2 py-2"
              >
                <p className={`text-xl font-extrabold ${item.color}`}>
                  {item.num}
                </p>
                <p className="text-sm text-gray-500">{item.label}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#0369a1] mt-2 opacity-70 text-center">
            This is normal — do not get discouraged. Stay consistent.
          </p>
        </div>
      </Accordion>

      {/* Summary */}
      <Accordion
        id="fazit"
        num="SUMMARY"
        icon="🏁"
        title="Session Summary"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <span className="text-2xl flex-shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-green-800 mb-2">
              You now have a complete host family search strategy.
            </p>
            <ul className="space-y-1">
              {[
                "Search works both ways — you apply and families apply to you",
                "AuPair.com is the primary platform — 150–200€/year, worth every cent",
                "Start free on AuPairWorld.net — test and refine your profile first",
                "Pay for AuPair.com only when profile is G2G-approved and 100% ready",
                "Facebook Groups are free and highly effective — join them all now",
                "Instagram presence works passively and costs nothing",
                "Every message must be personalized — mention the children's names",
                "Send 5–10 messages per day, reply within 2 hours",
                "Expect 50–100 messages before finding your family — stay consistent",
                "Red flags: no photo, inactive profile, payment requests, overwork expectations",
              ].map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-green-700 flex items-start gap-2"
                >
                  <span className="flex-shrink-0 mt-0.5">›</span> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Accordion>

      {/* Tasks */}
      <Accordion
        id="aufgaben"
        num="TASKS"
        icon="📋"
        title="Your Tasks — Session 03"
        openId={openSection}
        onToggle={toggle}
      >
        <div className="pt-1">
          <TaskList
            tasks={[
              <>
                <strong>Create free account on AuPairWorld.net</strong> — upload
                all photos and complete every profile field
              </>,
              <>
                <strong>Join 5 Facebook Au Pair Germany groups</strong> — write
                down the group names and post your introduction
              </>,
              <>
                <strong>Create your Au Pair Instagram account</strong> — set up
                bio, upload profile photo and first post
              </>,
              <>
                <strong>Write your first personalized message</strong> — using
                the formula from Section 05. Bring it to the next session for
                G2G review before sending.
              </>,
              <>
                <strong>Do NOT pay for AuPair.com yet</strong> — wait until your
                profile is reviewed and approved by G2G in the next session.
              </>,
            ]}
          />
        </div>
      </Accordion>
    </div>
  );
};

export default AuPairLecture03HostFamilySearch;
